import React from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ChevronRight,
  CircleDot,
  CheckCircle2,
  Lock,
  XCircle,
} from "lucide-react";
import { AgentTask, AgentTaskStatus } from "@forgepoint/shared";
import { getTaskActionDisplayName } from "@/modules/agents/components/helpers/richActionHelpers";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import {
  formatOwnersLabel,
  getHumanOwnerLabel,
  isAwaitingHumanAction,
} from "./opportunityTaskUtils";

interface OpportunityTaskRowProps {
  task: AgentTask;
  agentName: string;
  onThreadClick: (threadId: string) => void;
  threadAccessible?: boolean;
}

/**
 * A single task row in the opportunity tasks section.
 * Accessible tasks open their thread from the entire row, including completed tasks.
 *
 * @param task - The agent task to render
 * @param agentName - Display name for agent owners
 * @param onThreadClick - Callback to navigate to a thread
 * @param threadAccessible - Whether the current user can open the task's thread. When false, the open-thread control is locked with a tooltip instead of navigating.
 */
export const OpportunityTaskRow: React.FC<OpportunityTaskRowProps> = ({
  task,
  agentName,
  onThreadClick,
  threadAccessible = true,
}) => {
  const awaitingHumanAction = isAwaitingHumanAction(task);
  const isCompleted = task.status === AgentTaskStatus.COMPLETED;
  const isCancelled = task.status === AgentTaskStatus.CANCELLED;
  const ownerLabel = formatOwnersLabel(task.owners, agentName) ?? "Unassigned";
  const taskKind = task.actionParameters
    ? getTaskActionDisplayName(task.actionParameters.actionId)
    : "General";
  const assigneeLabel = awaitingHumanAction
    ? `Awaiting action from ${getHumanOwnerLabel(task.owners) ?? "the assigned user"}`
    : ownerLabel;

  const rowContent = (
    <>
      <TaskStatusIcon
        status={task.status}
        awaitingHumanAction={awaitingHumanAction}
      />
      <span className="min-w-0 flex-1 space-y-1">
        <span className="block break-words text-sm font-medium leading-5">
          {task.subject || "Untitled task"}
        </span>
        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          <span className="shrink-0 text-[10.5px] font-semibold uppercase tracking-wider">
            {taskKind}
          </span>
          <span
            className={cn(
              "min-w-0 break-words",
              awaitingHumanAction && "text-warning-foreground",
            )}
          >
            {assigneeLabel}
          </span>
          <time
            dateTime={task.updatedAt.toISOString()}
            title={task.updatedAt.toLocaleString()}
            className="ml-auto whitespace-nowrap tabular-nums"
          >
            {formatDistanceToNow(task.updatedAt, { addSuffix: true })}
          </time>
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {!awaitingHumanAction && <TaskStatusBadge status={task.status} />}
        {!threadAccessible && (
          <>
            <Lock className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="sr-only">Private conversation: no access</span>
          </>
        )}
        {task.threadId && threadAccessible && (
          <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-foreground" />
        )}
      </span>
    </>
  );
  const rowClassName = cn(
    "group flex w-full items-center gap-3 px-3 py-3 text-left transition-colors",
    (isCompleted || isCancelled) && "text-muted-foreground",
  );

  if (task.threadId && threadAccessible) {
    const threadId = task.threadId;
    return (
      <button
        type="button"
        onClick={() => onThreadClick(threadId)}
        className={cn(
          rowClassName,
          "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        )}
      >
        {rowContent}
      </button>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={rowClassName}
          tabIndex={threadAccessible ? undefined : 0}
        >
          {rowContent}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        {threadAccessible
          ? "No linked conversation"
          : "You don't have access to this private conversation"}
      </TooltipContent>
    </Tooltip>
  );
};

interface TaskStatusIconProps {
  status: AgentTaskStatus;
  awaitingHumanAction: boolean;
}

interface TaskStatusBadgeProps {
  status: AgentTaskStatus;
}

/** Renders a static task icon; in-progress tasks may be waiting for approval. */
function TaskStatusIcon({ status, awaitingHumanAction }: TaskStatusIconProps) {
  if (awaitingHumanAction) {
    return <CircleDot className="h-4 w-4 text-warning shrink-0" />;
  }
  switch (status) {
    case AgentTaskStatus.PENDING:
      return <CircleDot className="h-4 w-4 text-muted-foreground shrink-0" />;
    case AgentTaskStatus.IN_PROGRESS:
      return <CircleDot className="h-4 w-4 text-muted-foreground shrink-0" />;
    case AgentTaskStatus.COMPLETED:
      return (
        <CheckCircle2 className="h-4 w-4 text-muted-foreground shrink-0" />
      );
    case AgentTaskStatus.CANCELLED:
      return <XCircle className="h-4 w-4 text-muted-foreground shrink-0" />;
    default:
      return <CircleDot className="h-4 w-4 text-muted-foreground shrink-0" />;
  }
}

/** Shows saved statuses for tasks outside the human-action list. */
function TaskStatusBadge({ status }: TaskStatusBadgeProps) {
  switch (status) {
    case AgentTaskStatus.PENDING:
      return (
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground border">
          Pending
        </span>
      );
    case AgentTaskStatus.IN_PROGRESS:
      return (
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground border border-border">
          In progress
        </span>
      );
    case AgentTaskStatus.COMPLETED:
      return (
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground border border-border">
          Done
        </span>
      );
    case AgentTaskStatus.CANCELLED:
      return (
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground border">
          Cancelled
        </span>
      );
    default:
      return null;
  }
}
